import { useCallback } from "react";

import { Action, Stack, StackState } from "types/generated";
import { AccountContext } from "views/AccountWrapper";
import useTypedContext from "hooks/useTypedContext";
import { AccessResult, hasAtLeastOneRequiredAction, NO_ACCESS_MESSAGE } from "utils/access";
import { AccountViewer } from "components/form/graphql/types";
import useAacFlags from "hooks/useAacFlags";

import useHasStackWriteAccess from "./useHasStackWriteAccess";

const DISCARD_ACCESS_TYPES = [Action.RunDiscard];

const hasDiscardAccess = (viewer: AccountViewer, spaceId: string) =>
  hasAtLeastOneRequiredAction(DISCARD_ACCESS_TYPES, viewer, spaceId);

/**
 * Hook that returns a function to check if the current user can discard a stack run.
 * A user needs write access to discard a stack run.
 * A user can only discard a stack run when the stack is in unconfirmed state.
 * @returns {(stack: Stack) => AccessResult} Function that takes a stack and returns an AccessResult indicating if user can discard its run
 */
const useCanDiscardStackRun = (): ((stack: Stack) => AccessResult) => {
  const { customAccessRoles } = useAacFlags();

  const hasStackWriteAccess = useHasStackWriteAccess();
  const { viewer } = useTypedContext(AccountContext);

  return useCallback(
    (stack: Stack) => {
      if (stack.state !== StackState.Unconfirmed) {
        return {
          result: false,
        };
      }

      // TODO: This is a temporary solution with spaceDetails id or space until we have proper types for query results
      const hasAccess =
        hasStackWriteAccess(stack) ||
        (customAccessRoles && hasDiscardAccess(viewer, stack.spaceDetails?.id || stack.space));

      if (hasAccess) {
        return {
          result: true,
        };
      }

      return {
        result: false,
        message: NO_ACCESS_MESSAGE,
      };
    },
    [viewer, hasStackWriteAccess, customAccessRoles]
  );
};

export default useCanDiscardStackRun;
